import { com } from '@eidu/web'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import getEntityTypes from './api/entity/type/getEntityTypes'
import EqualityHashMap from '../util/EqualityHashMap'
import sortedBy from '../util/sort/sortedBy'
import equalityHashMapStructuralSharing from './equalityHashMapStructuralSharing'
import fetchAllPages from './fetchAllPages'
import { entityTypeKeys } from './queryKeys'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType

const PAGE_SIZE = 500

export const EMPTY_ENTITY_TYPES: ReadonlyMap<EntityTypeId, EntityType> = new EqualityHashMap()

export const fetchAllEntityTypes = async (): Promise<ReadonlyMap<EntityTypeId, EntityType>> => {
  const responses = await fetchAllPages(PAGE_SIZE, (pageIndex) => getEntityTypes({ pageIndex, pageSize: PAGE_SIZE }))
  const entries = responses.flatMap((response) =>
    response.documents.asJsReadonlyArrayView().map((it): [EntityTypeId, EntityType] => [it.id, it])
  )
  return new EqualityHashMap(entries)
}

const useEntityTypes = () => {
  const query = useQuery({
    queryKey: entityTypeKeys.list(),
    queryFn: fetchAllEntityTypes,
    structuralSharing: equalityHashMapStructuralSharing,
  })

  return {
    entityTypes: query.data,
    isLoading: query.isLoading,
    error: query.error,
  }
}

export const useSortedEntityTypes = (): EntityType[] | undefined => {
  const { entityTypes } = useEntityTypes()
  return useMemo(
    () => (entityTypes ? sortedBy(Array.from(entityTypes.values()), (it) => it.name) : undefined),
    [entityTypes]
  )
}

export default useEntityTypes
